import React, { useState, useEffect } from 'react';
import { Table, Input, Tag, Checkbox, Button, InputNumber, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import callApi from '../../Api/config';
import { PARTNER_SERVICES } from '../../constants/EntityName';
import { InvertFillButton } from '../../components/InvertFillButton';
import { toastError, toastSuccess, toastWarning } from '../../service/Toast';

interface PartnerPackageSelectionProps {
  partnerId: string;
  onClose: () => void;
  onSuccess: () => void;
  onSelectionSubmit?: (items: any[]) => void;
}

const PartnerPackageSelection: React.FC<PartnerPackageSelectionProps> = ({
  partnerId,
  onClose,
  onSuccess,
  onSelectionSubmit,
}) => {
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [customPrices, setCustomPrices] = useState<Record<string, number | null>>({});
  const [readyToShipStatus, setReadyToShipStatus] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchAvailableServices();
  }, []);

  const fetchAvailableServices = async () => {
    setLoading(true);
    try {
      // Fetch global services
      const globalRes = await callApi(`${PARTNER_SERVICES}?isGlobal=true&page=1&limit=200`, 'GET');

      // If partnerId exists, fetch already assigned services
      let assignedIds = new Set();
      if (partnerId) {
        const assignedRes = await callApi(
          `${PARTNER_SERVICES}?partnerId=${partnerId}&page=1&limit=200`,
          'GET'
        );
        if (assignedRes?.data?.data?.results) {
          assignedIds = new Set(assignedRes.data.data.results.map((item: any) => item.packageId));
        }
      }

      if (globalRes?.status && globalRes.data?.data?.results) {
        const globals = globalRes.data.data.results;
        // Filter out already assigned packages
        const availableServices = globals.filter((item: any) => !assignedIds.has(item.packageId));
        setServices(availableServices);
      }
    } catch (error) {
      console.error('Failed to fetch services', error);
      toastError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomPriceChange = (id: string, value: number | null) => {
    setCustomPrices(prev => ({
      ...prev,
      [id]: value,
    }));
  };

  // Helper: get the effective custom price for a service (user-set or default fee)
  const getEffectivePrice = (item: any): number | undefined => {
    const manualPrice = customPrices[item.id];
    if (manualPrice !== undefined && manualPrice !== null) return manualPrice;
    const defaultFee = Number(item.documitraFee);
    return !isNaN(defaultFee) && defaultFee > 0 ? defaultFee : undefined;
  };

  const handleReadyToShipChange = (id: string, checked: boolean) => {
    setReadyToShipStatus(prev => ({
      ...prev,
      [id]: checked,
    }));
  };

  const handleAssignSelected = async () => {
    if (selectedRowKeys.length === 0) {
      toastWarning('Please select at least one service');
      return;
    }

    // Local Selection Mode (for Create Partner flow)
    if (onSelectionSubmit) {
      const itemsToAssign = services.filter(s => selectedRowKeys.includes(s.id));
      const formattedItems = itemsToAssign.map(item => ({
        ...item,
        // Default to documitraFee if no custom price was set
        customPrice: getEffectivePrice(item),
        readyToShip: readyToShipStatus[item.id],
        package: item.package || {
          name: 'Unknown Package',
          service: { name: 'Unknown Service' },
        },
      }));

      onSelectionSubmit(formattedItems);
      onClose();
      return;
    }

    setLoading(true);
    try {
      const itemsToAssign = services.filter(s => selectedRowKeys.includes(s.id));

      const promises = itemsToAssign.map(item => {
        const payload = {
          partnerId: partnerId,
          packageId: item.packageId,
          name: item.name,
          documitraFee: item.documitraFee,
          // Default to documitraFee if admin did not set a custom price
          customPrice: getEffectivePrice(item),
          readyToShip: readyToShipStatus[item.id],
          status: 'Active',
        };
        return callApi(PARTNER_SERVICES, 'POST', payload);
      });

      await Promise.all(promises);
      toastSuccess(`${itemsToAssign.length} service(s) assigned successfully`);
      onSuccess();
      onClose();
    } catch (error) {
      toastError('Failed to assign services');
    } finally {
      setLoading(false);
    }
  };

  // Helper to try parsing JSON strings if necessary
  const parseValue = (val: any) => {
    if (typeof val === 'string') {
      try {
        return JSON.parse(val);
      } catch (e) {
        return val; // return as string if not valid JSON
      }
    }
    return val;
  };

  const getParameterTags = (record: any) => {
    const params: { label: string; values: string[] }[] = [];

    // We check for these specific keys as requested
    const keysToCheck = [
      { key: 'applyingFrom', label: 'Applying From' },
      { key: 'citizenship', label: 'Citizenship' },
      { key: 'destination', label: 'Destination' },
    ];

    keysToCheck.forEach(({ key, label }) => {
      let val = record[key];
      // Also check inside package if not found at root
      if (!val && record.package) {
        val = record.package[key];
      }

      if (val) {
        const parsed = parseValue(val);
        const values = Array.isArray(parsed) ? parsed : [String(parsed)];
        params.push({ label, values });
      }
    });

    return params;
  };

  const columns = [
    {
      title: 'Service Name',
      dataIndex: 'name',
      key: 'name',
      width: 300,
      filteredValue: searchText ? [searchText] : null,
      onFilter: (value: any, record: any) => {
        return (
          record.name?.toLowerCase().includes(value.toLowerCase()) ||
          record.package?.name?.toLowerCase().includes(value.toLowerCase())
        );
      },
      render: (name: string, record: any) => (
        <div style={{ fontWeight: 500, color: 'black' }}>{record.name || record.package?.name}</div>
      ),
    },
    {
      title: 'Parameters',
      key: 'parameters',
      width: 250,
      render: (record: any) => {
        const params = getParameterTags(record);

        if (params.length === 0) {
          return <span style={{ color: '#8c8c8c' }}>—</span>;
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {params.map((param, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip
                  title={
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {param.values.map((v, i) => (
                        <Tag key={i} style={{ margin: 0 }}>
                          {v}
                        </Tag>
                      ))}
                    </div>
                  }
                  color="white"
                  overlayInnerStyle={{ color: 'black' }}
                >
                  <span
                    style={{
                      fontSize: '13px',
                      color: '#1890ff',
                      cursor: 'pointer',
                      borderBottom: '1px dashed #1890ff',
                    }}
                  >
                    {param.label} ({param.values.length})
                  </span>
                </Tooltip>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: 'DEFAULT FEE',
      dataIndex: 'documitraFee',
      key: 'documitraFee',
      width: 120,
      render: (fee: any) => {
        const numFee = Number(fee);
        return (
          <span style={{ fontWeight: 500 }}>
            {!isNaN(numFee) && fee !== null && fee !== undefined ? `$${numFee.toFixed(2)}` : '—'}
          </span>
        );
      },
    },
    {
      title: 'CUSTOM PRICE',
      key: 'customPrice',
      width: 160,
      render: (record: any) => {
        const isSelected = selectedRowKeys.includes(record.id);
        const value = customPrices[record.id];
        return (
          <InputNumber
            placeholder={
              isSelected
                ? `Default: $${Number(record.documitraFee || 0).toFixed(2)}`
                : 'Select to set'
            }
            prefix="$"
            style={{ width: '100%', borderColor: isSelected && !value ? '#faad14' : undefined }}
            value={value}
            onChange={val => handleCustomPriceChange(record.id, val)}
            min={0}
            disabled={!isSelected}
          />
        );
      },
    },
    {
      title: 'READY TO SHIP',
      key: 'readyToShip',
      width: 120,
      align: 'center' as const,
      render: (record: any) => {
        // Check if manually set, otherwise check package default
        const isChecked =
          readyToShipStatus[record.id] !== undefined
            ? readyToShipStatus[record.id]
            : record.package?.readyToShip === true || record.readyToShip === true;

        return (
          <Checkbox
            checked={isChecked}
            onChange={e => handleReadyToShipChange(record.id, e.target.checked)}
          />
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      const newKeys = selectedKeys as string[];
      setSelectedRowKeys(newKeys);

      // Auto-populate custom price with documitraFee when a service is first selected
      setCustomPrices(prev => {
        const updated = { ...prev };
        newKeys.forEach(key => {
          // Only set if not already manually configured by admin
          if (updated[key] === undefined || updated[key] === null) {
            const service = services.find(s => s.id === key);
            const fee = Number(service?.documitraFee);
            if (!isNaN(fee) && fee > 0) {
              updated[key] = fee;
            }
          }
        });
        return updated;
      });
    },
  };

  const filteredServices = searchText
    ? services.filter(
        s =>
          s.name?.toLowerCase().includes(searchText.toLowerCase()) ||
          s.package?.name?.toLowerCase().includes(searchText.toLowerCase())
      )
    : services;

  const totalPrice = selectedRowKeys.reduce((sum, key) => {
    const service = services.find(s => s.id === key);
    const fee = Number(service?.documitraFee);
    return sum + (!isNaN(fee) ? fee : 0);
  }, 0);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#f5f7fb',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px clamp(20px, 4vw, 56px)',
          borderBottom: '1px solid #e8edf3',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '24px',
          background: '#fff',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            width: '100%',
            maxWidth: '1440px',
            margin: '0 auto',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h2
              style={{
                margin: 0,
                fontSize: '22px',
                lineHeight: 1.25,
                fontWeight: 700,
                color: '#172b4d',
              }}
            >
              Assign Services
            </h2>
            <p style={{ margin: '3px 0 0', color: '#6b778c', fontSize: '14px' }}>
              Select and configure services for this Agent
            </p>
          </div>
          <div style={{ marginLeft: 'auto', width: 'min(100%, 410px)' }}>
            <Input
              placeholder="Search services..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              allowClear
              style={{ width: '100%', borderRadius: '8px' }}
              size="large"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div
        style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '18px clamp(20px, 4vw, 56px)' }}
      >
        <div style={{ maxWidth: '1440px', margin: '0 auto' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              marginBottom: '12px',
            }}
          >
            <div style={{ color: '#42526e', fontSize: '14px', fontWeight: 600 }}>
              Available Services{' '}
              <span style={{ color: '#7a869a', fontWeight: 400 }}>({filteredServices.length})</span>
            </div>
            <div
              style={{
                padding: '5px 10px',
                borderRadius: '999px',
                background: selectedRowKeys.length ? '#e6f4ff' : '#edf1f5',
                color: selectedRowKeys.length ? '#096dd9' : '#6b778c',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              {selectedRowKeys.length} selected
            </div>
          </div>
          <div
            style={{
              background: '#fff',
              border: '1px solid #e7ecf2',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 4px 16px rgba(9, 30, 66, 0.05)',
            }}
          >
            <Table
              dataSource={filteredServices}
              columns={columns}
              rowKey="id"
              loading={loading}
              rowSelection={rowSelection}
              pagination={false}
              scroll={{ x: 950, y: 'calc(100vh - 300px)' }}
              size="middle"
              locale={{ emptyText: 'No available services found.' }}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '12px clamp(20px, 4vw, 56px)',
          borderTop: '1px solid #e8edf3',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#fff',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '1440px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <div style={{ color: '#6b778c', fontSize: '14px' }}>
            {selectedRowKeys.length} service{selectedRowKeys.length !== 1 ? 's' : ''} selected
            {/* {selectedRowKeys.length > 0 && (
              <span style={{ marginLeft: '8px', color: '#172b4d', fontWeight: 600 }}>
                • Default total: ${totalPrice.toFixed(2)}
              </span>
            )} */}
          </div>
          <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
            <InvertFillButton variant="white" size="lg" fontWeight="semibold" onClick={onClose}>
              Cancel
            </InvertFillButton>
            <InvertFillButton
              variant="blue"
              size="lg"
              fontWeight="semibold"
              onClick={handleAssignSelected}
              disabled={selectedRowKeys.length === 0 || loading}
              aria-busy={loading}
            >
              {loading
                ? 'Assigning...'
                : `${onSelectionSubmit ? 'Add' : 'Assign'} Selected (${selectedRowKeys.length})`}
            </InvertFillButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerPackageSelection;
